import { Head } from "@inertiajs/react"

import Navbar from "@/components/ttt/Navbar"
import Footer from "@/components/ttt/Footer"
import ProductCard from "@/components/ttt/ProductCard"
import PresaleCard from "@/components/ttt/PresaleCard"
import Logo from "@/components/ttt/Logo"
import type {
  TttChrome,
  TttCardItem,
  TttCardI18n,
  TttPresaleItem,
  TttPresaleI18n,
} from "@/components/ttt/types"

interface Props {
  chrome: TttChrome
  logoSrc: string
  logoBackSrc: string
  publishedLabel: string
  presaleItems: TttPresaleItem[]
  presaleI18n: TttPresaleI18n
  items: TttCardItem[]
  holidaySentence: string | null
  i18n: TttCardI18n
}

export default function Home({
  chrome,
  logoSrc,
  logoBackSrc,
  publishedLabel,
  presaleItems,
  presaleI18n,
  items,
  holidaySentence,
  i18n,
}: Props) {
  const holiday = holidaySentence !== null
  const backpackSrc = chrome.images.backpack

  return (
    <>
      <Head>
        <title>TTT</title>
      </Head>

      <Navbar chrome={chrome} isHome />

      {/* Hero */}
      <section className="relative lg:h-screen flex items-center justify-center font-ttt">
        {/* `w-full` sur mobile : sans elle, cet élément flex se dimensionne sur son
            contenu, dont l'enfant demande `w-full` — référence circulaire que le
            navigateur résout à 0, et le logo disparaît. Sur desktop `lg:w-auto`
            restaure la largeur au contenu, que `lg:w-[450px]` fixe côté enfant. */}
        <div
          className={`w-full lg:w-auto lg:pt-0 lg:px-48 flex items-center lg:flex-row flex-col ${
            presaleItems.length > 0 ? "justify-between" : "justify-center"
          }`}
        >
          <div className="flex items-center justify-center h-screen lg:h-auto w-full lg:w-[450px] lg:mr-24">
            <Logo frontSrc={logoSrc} backSrc={logoBackSrc} />
          </div>
          {presaleItems.length > 0 && (
            <div className="flex flex-col items-center justify-center gap-16 px-4 mb-48 h-1/2 w-full lg:mb-0 lg:px-0 lg:flex-1 lg:max-w-2xl">
              {presaleItems.map((item) => (
                <PresaleCard
                  key={item.id}
                  item={item}
                  dotsSrc={chrome.images.footer_dots}
                  i18n={presaleI18n}
                />
              ))}
            </div>
          )}
        </div>

        {/* Scroll-down cue (port of the ttt--scroll-down controller). */}
        <button
          type="button"
          aria-label="Défiler"
          onClick={() =>
            window.scrollBy({ top: window.innerHeight, left: 0, behavior: "smooth" })
          }
          className="hidden lg:block absolute inset-x-0 bottom-10 text-center cursor-pointer"
        >
          <i className="fa-solid fa-arrow-down animate-bounce text-4xl text-primary" />
        </button>
      </section>

      {/* Divider bar */}
      <div className="flex items-center">
        <div className="h-6 w-1/2 bg-contour text-right text-light uppercase font-ttt pr-2">
          {publishedLabel}
        </div>
        <div className="h-6 w-20 bg-gradient-to-br from-contour via-contour to-transparent clip-diagonal-right" />
      </div>

      {/* Catalog grid */}
      <div className="max-w-5xl mx-auto flex flex-col py-12 relative h-full px-6 font-ttt">
        {holidaySentence ? (
          <div className="flex items-center justify-center py-24">
            <h1 className="text-4xl text-midgray">{holidaySentence}</h1>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-x-8 gap-y-16 h-full sm:grid-cols-2 sm:gap-x-12 sm:gap-y-16 lg:gap-x-16 xl:grid-cols-3">
            {items.map((item) => (
              <ProductCard
                key={item.id}
                item={item}
                backpackSrc={backpackSrc}
                holiday={holiday}
                i18n={i18n}
              />
            ))}
          </div>
        )}
      </div>

      <Footer chrome={chrome} />
    </>
  )
}
